// Admin-Panel › Limits: Metadaten aller Plattform-Limits und reine Umrechnungsfunktionen
// (Anzeige-Einheit ↔ Basiseinheit des Vertrags: Bytes, ms, bit/s, Stunden, Anzahl).
import { DEFAULT_LIMITS, type PlatformLimitKey, type PlatformLimits } from '@lagerfeuer/shared';

export const BYTE_UNITS = ['B', 'KiB', 'MiB', 'GiB'] as const;
export type ByteUnit = (typeof BYTE_UNITS)[number];
/** Anzeige-Einheiten. `count` = Anzahl/Zeichen ohne Umrechnung. */
export type DisplayUnit = ByteUnit | 's' | 'kbit/s' | 'h' | 'count';

/** Faktor Anzeige → Basiseinheit. */
export const UNIT_FACTORS: Record<DisplayUnit, number> = {
  B: 1,
  KiB: 1024,
  MiB: 1024 ** 2,
  GiB: 1024 ** 3,
  s: 1000,
  'kbit/s': 1000,
  h: 1,
  count: 1,
};

export const LIMIT_GROUPS = [
  { id: 'uploads', label: 'Uploads & Medien' },
  { id: 'messages', label: 'Nachrichten' },
  { id: 'community', label: 'Server & Community' },
  { id: 'voice', label: 'Sprache & Video' },
  { id: 'account', label: 'Konto' },
  { id: 'automation', label: 'Automatisierung & Aufbewahrung' },
] as const;
export type LimitGroupId = (typeof LIMIT_GROUPS)[number]['id'];

/** `zeroUnlimited`: 0 bedeutet „unbegrenzt" (explizit je Feld, abgeglichen mit packages/shared/src/limits.ts). */
export type LimitFieldMeta =
  | { kind: 'bytes'; label: string; group: LimitGroupId; min: number; zeroUnlimited: boolean; hint?: string }
  | {
      kind: 'number';
      label: string;
      group: LimitGroupId;
      min: number;
      unit: Exclude<DisplayUnit, ByteUnit>;
      unitLabel: string;
      zeroUnlimited: boolean;
      hint?: string;
    }
  | { kind: 'boolean'; label: string; group: LimitGroupId; hint?: string }
  | { kind: 'preset'; label: string; group: LimitGroupId; hint?: string };

const count = (label: string, group: LimitGroupId, unitLabel = 'Anzahl', min = 0): LimitFieldMeta => ({
  kind: 'number',
  label,
  group,
  min,
  unit: 'count',
  unitLabel,
  zeroUnlimited: false,
});
const bytes = (label: string, group: LimitGroupId, min = 0): LimitFieldMeta => ({ kind: 'bytes', label, group, min, zeroUnlimited: false });
/** Markiert ein Zahlenfeld als „0 = unbegrenzt". */
const unl = (meta: LimitFieldMeta): LimitFieldMeta => (meta.kind === 'bytes' || meta.kind === 'number' ? { ...meta, zeroUnlimited: true } : meta);

/**
 * Jeder Schlüssel von PlatformLimits – der Record-Typ erzwingt Vollständigkeit schon beim Kompilieren.
 * „0 = unbegrenzt" (`unl`) laut packages/shared/src/limits.ts: alle `max*`-Zahlen außer maxUploadBytes/maxMessageLength
 * sowie superReactionsPerDay. Nicht: scheduledMessageMax, remindersMax, exportRetentionHours, orphanUploadHours.
 */
export const LIMIT_FIELDS: Record<PlatformLimitKey, LimitFieldMeta> = {
  // Uploads & Medien
  maxUploadBytes: bytes('Max. Dateigröße je Upload', 'uploads', 1),
  maxAttachmentsPerMessage: unl(count('Max. Anhänge je Nachricht', 'uploads')),
  maxAvatarBytes: unl(bytes('Max. Avatar/Banner/Server-Icon', 'uploads')),
  maxEmojiBytes: unl(bytes('Max. Emoji-Dateigröße', 'uploads')),
  maxStickerBytes: unl(bytes('Max. Sticker-Dateigröße', 'uploads')),
  maxSoundBytes: unl(bytes('Max. Sound-Dateigröße', 'uploads')),
  maxSoundDurationMs: { kind: 'number', label: 'Max. Sound-Länge', group: 'uploads', min: 0, unit: 's', unitLabel: 'Sekunden', zeroUnlimited: true },
  // Nachrichten
  maxMessageLength: count('Max. Zeichen je Nachricht', 'messages', 'Zeichen', 1),
  maxPinsPerChannel: unl(count('Max. angeheftete Nachrichten je Kanal', 'messages')),
  maxReactionsPerMessage: unl(count('Max. verschiedene Reaktionen je Nachricht', 'messages')),
  superReactionsPerDay: unl(count('Super-Reaktionen je Nutzer und Tag', 'messages', 'Anzahl')),
  maxGroupDmParticipants: unl(count('Max. Teilnehmer je Gruppen-DM', 'messages', 'Personen')),
  // Server & Community
  maxServersPerUser: unl(count('Max. Server je Nutzer', 'community')),
  maxEmojisPerServer: unl(count('Max. Emojis je Server', 'community')),
  maxStickersPerServer: unl(count('Max. Sticker je Server', 'community')),
  maxSoundsPerServer: unl(count('Max. Sounds je Server', 'community')),
  maxWebhooksPerChannel: unl(count('Max. Webhooks je Kanal', 'community')),
  // Sprache & Video
  maxAudioBitrate: { kind: 'number', label: 'Max. Audio-Bitrate', group: 'voice', min: 0, unit: 'kbit/s', unitLabel: 'kbit/s', zeroUnlimited: true },
  maxVoiceUsers: unl(count('Max. Teilnehmer je Sprachkanal', 'voice', 'Personen')),
  screenshareMaxPreset: { kind: 'preset', label: 'Höchste Bildschirmübertragung', group: 'voice' },
  // Konto
  registrationOpen: { kind: 'boolean', label: 'Registrierung offen', group: 'account', hint: 'Aus: Nur Admins legen Konten an.' },
  maxBioLength: unl(count('Max. Länge der Profil-Bio', 'account', 'Zeichen')),
  maxCustomCssBytes: unl(bytes('Max. eigenes CSS', 'account')),
  maxApiTokensPerUser: unl(count('Max. API-Tokens je Nutzer', 'account')),
  maxBotsPerUser: unl(count('Max. Bots je Nutzer', 'account')),
  // Automatisierung & Aufbewahrung
  scheduledMessageMax: count('Offene geplante Nachrichten je Nutzer', 'automation'),
  remindersMax: count('Erinnerungen je Nutzer', 'automation'),
  exportRetentionHours: { kind: 'number', label: 'Exporte aufbewahren', group: 'automation', min: 0, unit: 'h', unitLabel: 'Stunden', zeroUnlimited: false },
  orphanUploadHours: {
    kind: 'number',
    label: 'Ungebundene Uploads löschen nach',
    group: 'automation',
    min: 0,
    unit: 'h',
    unitLabel: 'Stunden',
    zeroUnlimited: false,
  },
};

export const LIMIT_KEYS = Object.keys(DEFAULT_LIMITS) as PlatformLimitKey[];

/** „0 = unbegrenzt" laut explizitem Flag in LIMIT_FIELDS. */
export function zeroMeansUnlimited(key: PlatformLimitKey): boolean {
  const meta = LIMIT_FIELDS[key];
  return (meta.kind === 'bytes' || meta.kind === 'number') && meta.zeroUnlimited;
}

/** Basiswert → Anzeigewert (höchstens 3 Nachkommastellen). */
export function toDisplay(value: number, unit: DisplayUnit): number {
  return Math.round((value / UNIT_FACTORS[unit]) * 1000) / 1000;
}

/** Anzeigewert (Zahl oder Eingabetext, Komma erlaubt) → ganzzahliger Basiswert; ungültig/negativ → null. */
export function fromDisplay(display: string | number, unit: DisplayUnit): number | null {
  const text = typeof display === 'number' ? String(display) : display.trim().replace(/\s/g, '').replace(',', '.');
  if (text === '' || !/^\d*\.?\d*$/.test(text) || text === '.') return null;
  const n = Number(text);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * UNIT_FACTORS[unit]);
}

/** Größte Byte-Einheit, die den Wert ganzzahlig darstellt (0 → MiB als handliche Voreinstellung). */
export function bestByteUnit(value: number): ByteUnit {
  if (value === 0) return 'MiB';
  for (let i = BYTE_UNITS.length - 1; i > 0; i--) {
    const unit = BYTE_UNITS[i];
    if (value >= UNIT_FACTORS[unit] && value % UNIT_FACTORS[unit] === 0) return unit;
  }
  return 'B';
}

/** Anzeigetext einer Zahl (deutsches Komma). */
export function displayText(value: number, unit: DisplayUnit): string {
  return String(toDisplay(value, unit)).replace('.', ',');
}

/** Nur geänderte Felder (Basiseinheit) für den PATCH. */
export function diffLimits(current: PlatformLimits, next: Partial<PlatformLimits>): Partial<PlatformLimits> {
  const out: Partial<Record<PlatformLimitKey, unknown>> = {};
  for (const key of LIMIT_KEYS) {
    if (key in next && next[key] !== undefined && next[key] !== current[key]) out[key] = next[key];
  }
  return out as Partial<PlatformLimits>;
}
